import { useMemo } from "react";

export type HistoryFilterControl =
  | "search"
  | "date"
  | "zap"
  | "app"
  | "folder"
  | "owner"
  | "refresh";

export type InlineHistoryFilterControl = Exclude<
  HistoryFilterControl,
  "search"
>;

const INLINE_FILTER_ORDER: InlineHistoryFilterControl[] = [
  "date",
  "zap",
  "app",
  "folder",
  "owner",
  "refresh",
];

export type FilterControlState = {
  showSearch: boolean;
  showInline: Record<InlineHistoryFilterControl, boolean>;
  visibleInlineControls: InlineHistoryFilterControl[];
  desktopGridColumns: string;
  tabletGridColumns: string;
};

export function getVisibleInlineFilterControls(
  hiddenFilterSet: ReadonlySet<HistoryFilterControl>
): InlineHistoryFilterControl[] {
  return INLINE_FILTER_ORDER.filter((control) => {
    return !hiddenFilterSet.has(control);
  });
}

export function getDesktopGridTemplateColumns(
  controls: InlineHistoryFilterControl[]
) {
  const columnCount = controls.length;
  if (columnCount === 0) {
    return "1fr";
  }

  const containsRefresh = controls.includes("refresh");
  const autoColumns = containsRefresh ? 1 : 0;
  const minmaxColumns = Math.min(columnCount - autoColumns, 5);

  const minmaxPart =
    minmaxColumns > 0 ? `repeat(${minmaxColumns}, minmax(100px, 1fr))` : "";
  const autoPart = containsRefresh ? " auto" : "";

  return `${minmaxPart}${autoPart}`;
}

export function getTabletGridTemplateColumns(
  controls: InlineHistoryFilterControl[]
) {
  const containsRefresh = controls.includes("refresh");
  const defaultColumnCount = 3;

  const wouldOrphanRefresh =
    containsRefresh && controls.length % defaultColumnCount === 1;

  if (!wouldOrphanRefresh) {
    return `repeat(${defaultColumnCount}, 1fr)`;
  }

  // Add an extra "auto" size column for the refresh control to avoid it being orphaned on a new row
  return `repeat(${defaultColumnCount}, 1fr) auto`;
}

export function getFilterControlState(
  hiddenFilters: HistoryFilterControl[] = [],
  isTeam: boolean
): FilterControlState {
  const hiddenFilterSet = new Set(hiddenFilters);

  if (!isTeam) {
    hiddenFilterSet.add("owner");
  }

  const visibleInlineControls = getVisibleInlineFilterControls(hiddenFilterSet);
  const desktopGridColumns = getDesktopGridTemplateColumns(
    visibleInlineControls
  );
  const tabletGridColumns = getTabletGridTemplateColumns(visibleInlineControls);
  const showInline = INLINE_FILTER_ORDER.reduce<
    Record<InlineHistoryFilterControl, boolean>
  >(
    (result, control) => {
      result[control] = !hiddenFilterSet.has(control);
      return result;
    },
    {} as Record<InlineHistoryFilterControl, boolean>
  );

  return {
    desktopGridColumns,
    showInline,
    showSearch: !hiddenFilterSet.has("search"),
    tabletGridColumns,
    visibleInlineControls,
  };
}

export function useFilterControlState(
  hiddenFilters: HistoryFilterControl[] = [],
  isTeam: boolean
): FilterControlState {
  return useMemo(
    () => getFilterControlState(hiddenFilters, isTeam),
    [hiddenFilters, isTeam]
  );
}
